import { AlertAcceptanceStatus, Roles } from '@shared/types'
import { CircularProgress, Tab, Tabs, Typography } from '@mui/material'
import React, { useCallback, useEffect, useMemo } from 'react'
import { TabContext, TabPanel } from '@mui/lab'

import makeStyles from '@mui/styles/makeStyles'
import { originalIcons } from '@shared/components/icons/icons'
import { useACL } from '@shared/hooks/use-acl.hook'
import { useAuditQuery } from './use-audit-query.hook'
import { usePersistedTabChange } from '@shared/hooks/use-persisted-tab-change'
import { useRouter } from '@shared/hooks/use-router'
import { AuditHeader } from '@modules/requirements/audit-header.component'
import { Actions } from '@shared/lib/reducer'
import { useStore } from '@shared/hooks/use-store.hook'
import { MaterialTableProps } from '@material-table/core'
import { useIsClientBasic } from '@shared/hooks/use-is-client-basic'
import { useStatusesQuery } from '@shared/hooks/use-statuses-query.hook'
import {
  RequirementsTable,
  TableRequirement,
} from './requirements-table.component'
import { useRequirementMutation } from './use-requirement-mutation'
import IDS from '@shared/onboarding/ids'
import Joyride from 'react-joyride'
import { ACL } from '@shared/components/acl.component'
import {
  previewPageSteps,
  previewPageStepsBasic,
} from '@shared/onboarding/steps'
import { onboardingConfig } from '@shared/onboarding/config'
import {
  OnboardingType,
  useOnboardingStore,
} from '@shared/onboarding/onboarding.store'
import { useQuery } from 'react-query'
import { apiJSON } from '@shared/utils/api/api.util'
import { sortBy } from 'lodash'
import ids from '@shared/onboarding/ids'

export type Group<Requirement> = {
  groupID: number
  order: number
  no: number
  name: string
  requirements: (Requirement & { no: string })[]
}

export function CompanyProfilePage({
  isPreviewMode,
}: {
  isPreviewMode: boolean
}) {
  const classes = useStyles()
  const { is } = useACL()
  const { isActive, setActive } = useOnboardingStore()
  const { dispatch } = useStore()
  const isClientBasic = useIsClientBasic()
  const { statuses, areStatusesLoading } = useStatusesQuery()

  const { data: scopesData } = useQuery<
    {
      scopeID: number
      order: number
    }[]
  >(`scopes`, () => apiJSON(`scopes`))

  const { query, navigate } = useRouter<{
    auditID: number
    companyID: number
  }>()
  const { companyID, auditID } = query

  const {
    currentTab,
    onTabChange,
    isLoading: isTabLoading,
  } = usePersistedTabChange(`currentTab.${companyID}.${auditID}`)

  const { companyProfile, audit, isLoading } = useAuditQuery(
    isPreviewMode,
    companyID,
    auditID
  )
  const { hideRequirement, acceptAlert, dontShowStatus } =
    useRequirementMutation(companyID, auditID, statuses)

  const isDataLoading = isLoading || areStatusesLoading

  const scopes = useMemo(
    () =>
      sortBy(
        isPreviewMode
          ? [
              ...(companyProfile?.assessedScopes || []).map((scope) => ({
                ...scope,
                isAudit: true,
              })),
              ...(companyProfile?.scopesInEsqula || []).map((scope) => ({
                ...scope,
                isAudit: false,
              })),
            ]
          : (audit?.scopedRequirements || []).map((scope) => ({
              ...scope,
              isAudit: true,
            })),
        ['order']
      ),
    [
      companyProfile?.assessedScopes,
      companyProfile?.scopesInEsqula,
      audit?.scopedRequirements,
      isPreviewMode,
    ]
  )

  const tabs = useMemo(() => {
    const orderMap = new Map(
      scopesData?.map((item) => [item.scopeID, item.order])
    )

    return sortBy(
      scopes.map(({ name, isAudit, id }) => ({ name, isAudit, id })),
      (scope) => orderMap.get(scope.id) ?? Infinity
    )
  }, [scopes, scopesData])

  const getActions = useCallback(
    (isAudit: boolean): MaterialTableProps<TableRequirement>['actions'] => {
      const actions: MaterialTableProps<TableRequirement>['actions'] = []

      const config = {
        preview: true,
        edit: false,
        dontShow: false,
        alert: is([
          Roles.Client,
          Roles.ClientBasic,
          Roles.ClientAdministrator,
          Roles.ClientEditor,
        ]),
      }

      if (isPreviewMode) {
        if (is([Roles.Admin, Roles.ClientAdministrator, Roles.ClientEditor])) {
          config.edit = true
          config.preview = false
        }
      } else {
        if (isAudit) {
          if (is([Roles.Admin, Roles.Auditor])) {
            config.preview = false
            config.edit = true
            config.dontShow = true
          }
        }
      }

      if (config.alert) {
        actions.push((rowData: TableRequirement) => ({
          icon: originalIcons.TaskAlt,
          tooltip: 'Usuń alert',
          hidden:
            !rowData.alert ||
            rowData.alert.acceptanceStatus === AlertAcceptanceStatus.Accepted,
          onClick: (event: React.MouseEvent<HTMLElement>, req: unknown) => {
            const requirement = req as TableRequirement
            acceptAlert({ requirement, isActive: isAudit })
          },
        }))
      }

      if (config.dontShow) {
        actions.push((rowData: TableRequirement) => {
          return {
            icon: originalIcons.VisibilityOff,
            tooltip: 'Nie pokazuj',
            onClick: (event: React.MouseEvent<HTMLElement>, data: unknown) => {
              hideRequirement({ requirement: data as TableRequirement })
            },
            disabled: rowData.isDraft === false,
          }
        })
      }

      if (config.preview) {
        actions.push({
          icon: originalIcons.Visibility,
          tooltip: 'Zobacz',
          iconProps: { id: IDS.OPEN_REQUIREMENT },
          onClick: (event: React.MouseEvent<HTMLElement>, req: unknown) => {
            const requirement = req as TableRequirement
            navigate(
              `/company-profile/${companyID}${auditID ? `/${auditID}` : ''}/${isAudit ? 'requirement' : 'esqula-requirement'}/${requirement.id}`
            )
          },
        })
      }

      if (config.edit) {
        actions.push({
          icon: originalIcons.Visibility,
          tooltip: 'Edytuj',
          iconProps: { id: IDS.OPEN_REQUIREMENT },
          onClick: (event: React.MouseEvent<HTMLElement>, req: unknown) => {
            const requirement = req as TableRequirement
            navigate(
              `/company-profile/${companyID}${auditID ? `/${auditID}` : ''}/${isAudit ? 'requirement' : 'esqula-requirement'}/${requirement.id}/edit`
            )
          },
        })
      }

      return actions
    },
    [dontShowStatus]
  )

  useEffect(() => {
    if (tabs.length > 0 && (+currentTab >= tabs.length || !currentTab)) {
      onTabChange(null, '0')
      dispatch({ type: Actions.CLEAR_EXPANDED_VALUES })
    }
  }, [tabs.length, currentTab])

  return !isDataLoading ? (
    <div>
      <ACL
        allowedRoles={[
          Roles.Client,
          Roles.ClientEditor,
          Roles.ClientAdministrator,
          Roles.ClientBasic,
        ]}
      >
        <Joyride
          {...onboardingConfig}
          steps={isClientBasic ? previewPageStepsBasic : previewPageSteps}
          continuous
          run={isActive(OnboardingType.PREVIEW_PAGE)}
          disableScrolling={true}
          callback={(data) => {
            if (data.status === 'finished' || data.status === 'skipped') {
              setActive(OnboardingType.PREVIEW_PAGE, false)
            }
          }}
        />
      </ACL>
      <AuditHeader
        companyProfile={companyProfile}
        audit={audit}
        isPreviewMode={isPreviewMode}
        draftsExists={false}
        currentTab={tabs[+currentTab]}
        currentScopeID={tabs[+currentTab]?.id}
        currentTabSelected={tabs[+currentTab]?.name}
        isSaveEnabled={false}
      />
      {isPreviewMode && !tabs.length ? (
        <Typography
          sx={{ textAlign: 'center', paddingY: '15px', fontWeight: 'bold' }}
        >
          Brak opublikowanych zakresów
        </Typography>
      ) : (
        <TabContext value={currentTab}>
          <div id={ids.TABS}>
            {isTabLoading ? (
              <CircularProgress
                sx={{ display: 'flex', justifySelf: 'center' }}
              />
            ) : (
              <Tabs
                value={currentTab}
                onChange={(...arg) => {
                  onTabChange(...arg)
                  dispatch({ type: Actions.CLEAR_EXPANDED_VALUES })
                }}
                variant="scrollable"
                scrollButtons="auto"
                textColor="primary"
                indicatorColor="primary"
              >
                {tabs.map(({ name, isAudit }, index) => (
                  <Tab
                    key={name}
                    label={name}
                    value={index.toString()}
                    className={classes.tab}
                    wrapped
                    iconPosition="end"
                    icon={
                      isAudit && !isClientBasic ? (
                        <originalIcons.Assessment
                          {...(index === 0 ? { id: ids.AUDIT_ICON } : {})}
                        />
                      ) : undefined
                    }
                  />
                ))}
              </Tabs>
            )}
          </div>
          {scopes.map((scope, index) => {
            const { isAudit, name, groups } = scope
            const actions = getActions(isAudit)
            return (
              <TabPanel
                key={name}
                value={index.toString()}
                style={{ padding: 0 }}
              >
                <RequirementsTable
                  isActive={isAudit}
                  auditID={auditID}
                  companyID={companyID}
                  groups={groups}
                  companyProfile={companyProfile!}
                  enableColumnStatus={!isClientBasic && isAudit}
                  statuses={statuses!}
                  enableColumnNo
                  enableGroupRowVerifiedCount={!isClientBasic && isAudit}
                  enableDontShow={!isPreviewMode && isAudit}
                  isLoading={isDataLoading}
                  isPreviewMode={isPreviewMode}
                  initiallyCollapsedWithButtons={true}
                  trackBy={`requirements:audit.${companyID}.${auditID}.${name}`}
                  actions={actions}
                  currentScopeID={tabs[+currentTab]?.id}
                />
              </TabPanel>
            )
          })}
        </TabContext>
      )}
    </div>
  ) : (
    <div className={classes.progress}>
      <CircularProgress />
    </div>
  )
}
export const useStyles = makeStyles((theme) => {
  return {
    tab: {
      paddingLeft: 14,
      paddingRight: 14,
    },
    progress: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24,
      flex: 1,
    },
  }
})
